import { observable, action, makeObservable } from "mobx";
import { observer } from "mobx-react";
import React from "react";
import { type Department, type UserGroup, type Shift, FleetRole } from "../../api";
import { ActionButton, Dropdown, type IDropdownOption } from "@fluentui/react";
import { defaultPageSize } from "../../utils/constants";

export interface FilterUserListProps {
    /** This callback function allows the filter to rerender
     * the parent component. */
    setUserListFilter(filter: FilterUserListQuery): void;
}

export type FilterUserListQuery = {
    filteredDepartments: string[];
    filteredFleetRoles: FleetRole[];
    filteredUserGroups: string[];
    filteredShifts: string[];
    isFiltered: boolean;
};

/**
 * A class which can filter the user list by departments, fleet roles,
 * group assignments and shifts.
 */
import { store } from "../../store";
import SearchableDropdown from "../atoms/searchable-dropdown";

@observer
export class FilterUserList extends React.Component<FilterUserListProps> {
    @observable private accessor filteredDepartments: string[] = [];
    @observable private accessor filteredFleetRoles: FleetRole[] = [];
    @observable private accessor filteredUserGroups: string[] = [];
    @observable private accessor filteredShifts: string[] = [];
    @observable private accessor isFiltered: boolean = false;

    constructor(props: FilterUserListProps) {
        super(props);
        makeObservable(this);
    }

    private setDepartmentFilter(filter: string): void {
        // Remove element if it is already clicked.
        if (this.filteredDepartments.includes(filter)) {
            this.filteredDepartments = this.filteredDepartments.filter((id) => id !== filter);
            this.setUserListFilter();
            return;
        }
        this.isFiltered = true;
        this.filteredDepartments = [filter, ...this.filteredDepartments];
        this.setUserListFilter();
    }

    private setFleetRolesFilter(filter: FleetRole): void {
        // Remove element if it is already clicked.
        if (this.filteredFleetRoles.includes(filter)) {
            this.filteredFleetRoles = this.filteredFleetRoles.filter((id) => id !== filter);
            this.setUserListFilter();
            return;
        }
        this.isFiltered = true;
        this.filteredFleetRoles = [filter, ...this.filteredFleetRoles];
        this.setUserListFilter();
    }

    private setShiftFilter(filter: string): void {
        // Remove element if it is already clicked.
        if (this.filteredShifts.includes(filter)) {
            this.filteredShifts = this.filteredShifts.filter((id) => id !== filter);
            this.setUserListFilter();
            return;
        }
        this.isFiltered = true;
        this.filteredShifts = [filter, ...this.filteredShifts];
        this.setUserListFilter();
    }

    private setUserGroupFilter(filter: string): void {
        // Remove element if it is already clicked.
        if (this.filteredUserGroups.includes(filter)) {
            this.filteredUserGroups = this.filteredUserGroups.filter((id) => id !== filter);
            this.setUserListFilter();
            return;
        }
        this.isFiltered = true;
        this.filteredUserGroups = [filter, ...this.filteredUserGroups];
        this.setUserListFilter();
    }

    @action.bound private clearFilter(): void {
        this.filteredDepartments = [];
        this.filteredShifts = [];
        this.filteredUserGroups = [];
        this.isFiltered = false;
        this.setUserListFilter();
    }

    private setUserListFilter(): void {
        const filterQuery: FilterUserListQuery = {
            filteredDepartments: this.filteredDepartments,
            filteredFleetRoles: this.filteredFleetRoles,
            filteredUserGroups: this.filteredUserGroups,
            filteredShifts: this.filteredShifts,
            isFiltered: this.isFiltered,
        };
        this.props.setUserListFilter(filterQuery);
    }

    public render(): JSX.Element {
        return (
            <>
                <SearchableDropdown
                    hideSearch
                    query={{ pageSize: defaultPageSize }}
                    placeholder={store.i18n.t("schema.user.department")}
                    repository={store.repositoryDepartments}
                    formatEntity={(department: Department) => department.label}
                    selectedKeys={this.filteredDepartments}
                    onChange={(departmentId: string | undefined) => {
                        if (departmentId === undefined) {
                            return;
                        }
                        this.setDepartmentFilter(departmentId);
                    }}
                    multiSelect
                />
                <Dropdown
                    styles={{ dropdown: { width: 200 } }}
                    options={Object.values(FleetRole).map((fleetRole) => {
                        return {
                            key: fleetRole,
                            text: store.i18n.t(`schema.fleetRole.${fleetRole}`),
                        };
                    })}
                    placeholder={store.i18n.t("schema.user.fleetRole")}
                    selectedKeys={this.filteredFleetRoles}
                    onChange={(_e, item?: IDropdownOption) => {
                        if (!item) {
                            return;
                        }
                        this.setFleetRolesFilter(item.key as FleetRole);
                    }}
                    multiSelect
                />
                <SearchableDropdown
                    hideSearch
                    query={{ pageSize: defaultPageSize }}
                    placeholder={store.i18n.t("schema.user.shift")}
                    repository={store.repositoryShifts}
                    formatEntity={(shift: Shift) => shift.label}
                    selectedKeys={this.filteredShifts}
                    onChange={(shiftId: string | undefined) => {
                        if (shiftId === undefined) {
                            return;
                        }
                        this.setShiftFilter(shiftId);
                    }}
                    multiSelect
                />
                <SearchableDropdown
                    hideSearch
                    query={{ pageSize: defaultPageSize }}
                    placeholder={store.i18n.t("schema.userGroup.userGroup")}
                    repository={store.repositoryUserGroups}
                    formatEntity={(userGroup: UserGroup) => userGroup.label}
                    selectedKeys={this.filteredUserGroups}
                    onChange={(userGroupId: string | undefined) => {
                        if (userGroupId === undefined) {
                            return;
                        }
                        this.setUserGroupFilter(userGroupId);
                    }}
                    multiSelect
                />
                <ActionButton
                    iconProps={{ iconName: "clearFilter" }}
                    allowDisabledFocus
                    disabled={!this.isFiltered}
                    checked={true}
                    onClick={this.clearFilter}
                ></ActionButton>
            </>
        );
    }
}
